import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface ConnectionRequest {
  id: string;
  tenantId: string;
  tenantName: string;
  requestedAt: string;
}

interface ComplianceSummary {
  summary: {
    totalRecords: number;
    compliantRecords: number;
    incompleteRecords: number;
    complianceRate: number;
    totalDocuments: number;
    pendingDocuments: number;
    completedDocuments: number;
    modelsInvolved: number;
  };
  recentActivity: {
    last30Days: number;
  };
}

export default function Dashboard() {
  const { user } = useAuth();

  const { data: compliance, isLoading } = useQuery({
    queryKey: ['compliance-summary'],
    queryFn: () => api.get<ComplianceSummary>('/compliance/summary'),
    enabled: user?.role !== 'model',
  });

  const summary = compliance?.data.summary;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p className="page-subtitle">Welcome back, {user?.email}</p>
      </div>

      {user?.role === 'model' ? (
        <ModelDashboard />
      ) : (
        <StudioDashboard
          summary={summary}
          recentActivity={compliance?.data.recentActivity}
          isLoading={isLoading}
          isSuperAdmin={user?.role === 'super_admin'}
        />
      )}
    </div>
  );
}

function ModelDashboard() {
  const queryClient = useQueryClient();

  // Fetch pending connection requests
  const { data: requestsData, isLoading: requestsLoading } = useQuery({
    queryKey: ['connection-requests'],
    queryFn: () => api.get<{ requests: ConnectionRequest[] }>('/models/connection-requests'),
  });

  // Approve/reject mutation
  const connectionMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      api.post(`/models/connection-requests/${id}/${action}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connection-requests'] });
    },
  });

  const requests = requestsData?.data.requests || [];

  return (
    <>
      {/* Getting Started Guide Link */}
      <div className="card guide-card" style={{ marginBottom: '1.5rem' }}>
        <h3>New to MediaRite?</h3>
        <p>Check out our getting started guide to learn how to use the platform.</p>
        <Link to="/guide/model" className="btn btn-primary">
          View Getting Started Guide
        </Link>
      </div>

      {requests.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3>Pending Connection Requests</h3>
          <p className="text-muted">Studios requesting to connect with you</p>
          <div className="connection-requests-list">
            {requests.map((request) => (
              <div key={request.id} className="connection-request-item">
                <div className="request-info">
                  <strong>{request.tenantName}</strong>
                  <span className="text-muted">
                    Requested {new Date(request.requestedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="request-actions">
                  <button
                    onClick={() => connectionMutation.mutate({ id: request.id, action: 'approve' })}
                    className="btn btn-small btn-primary"
                    disabled={connectionMutation.isPending}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => connectionMutation.mutate({ id: request.id, action: 'reject' })}
                    className="btn btn-small btn-secondary"
                    disabled={connectionMutation.isPending}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="card">
          <h3>My Profile</h3>
          <p>Manage your model profile and documents</p>
          <Link to="/my-profile" className="btn btn-secondary">
            View Profile
          </Link>
        </div>

        <div className="card">
          <h3>My Documents</h3>
          <p>View and sign release documents</p>
          <Link to="/documents" className="btn btn-secondary">
            View Documents
          </Link>
        </div>
      </div>

      {requestsLoading && (
        <div className="loading">Checking for connection requests...</div>
      )}
    </>
  );
}

function StudioDashboard({
  summary,
  recentActivity,
  isLoading,
  isSuperAdmin,
}: {
  summary?: ComplianceSummary['summary'];
  recentActivity?: ComplianceSummary['recentActivity'];
  isLoading: boolean;
  isSuperAdmin: boolean;
}) {
  if (isLoading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <>
      {/* Getting Started Guide Link */}
      <div className="card guide-card" style={{ marginBottom: '1.5rem' }}>
        <h3>New to MediaRite?</h3>
        <p>Check out our getting started guide to learn how to manage your studio.</p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link to="/guide/studio" className="btn btn-primary">
            View Studio Guide
          </Link>
          {isSuperAdmin && (
            <Link to="/guide/super-admin" className="btn btn-secondary">
              View Super Admin Guide
            </Link>
          )}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{summary?.complianceRate || 0}%</div>
          <div className="stat-label">Compliance Rate</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary?.totalDocuments || 0}</div>
          <div className="stat-label">Total Documents</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary?.pendingDocuments || 0}</div>
          <div className="stat-label">Pending Signatures</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{summary?.modelsInvolved || 0}</div>
          <div className="stat-label">Active Models</div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <h3>Compliance Overview</h3>
          <div className="compliance-overview">
            <div className="compliance-item compliant">
              <span className="count">{summary?.compliantRecords || 0}</span>
              <span className="label">Compliant</span>
            </div>
            <div className="compliance-item incomplete">
              <span className="count">{summary?.incompleteRecords || 0}</span>
              <span className="label">Incomplete</span>
            </div>
          </div>
          <Link to="/compliance" className="btn btn-secondary">
            View Details
          </Link>
        </div>

        <div className="card">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/productions/new" className="btn btn-primary">
              New Production
            </Link>
            <Link to="/models" className="btn btn-secondary">
              Manage Models
            </Link>
            <Link to="/documents/new" className="btn btn-secondary">
              Create Document
            </Link>
          </div>
        </div>

        <div className="card">
          <h3>Recent Activity</h3>
          <p className="activity-stat">
            <strong>{recentActivity?.last30Days || 0}</strong> records
            created in the last 30 days
          </p>
          <Link to="/compliance" className="btn btn-secondary">
            View All Activity
          </Link>
        </div>
      </div>
    </>
  );
}
